import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
// import InfoIcon from "@mui/icons-material/Info";
import { StatusType } from "Dashboard/scenes/HumanResources/domain/StatusType";
import { SalaryType } from "Dashboard/scenes/HumanResources/domain/SalaryType";
import { FormatDateIso } from "utils/dateUtils";
import Loader from "components/Loader/Loader";
import { useOnClickOutside } from "hooks/ClickOutside";
import { GenderType } from "Dashboard/scenes/HumanResources/domain/GenderType";
import { IUpdateEmployeeRequestParams } from "Dashboard/scenes/HumanResources/domain/IUpdateEmployeeRequestParams";
import { debounce } from "lodash";
import {
  getEmployeeDetails,
  selectState,
  updateEmployeeDetailsTemporarily,
  updateEmployeeDetails,
} from "../employeeSlice";
import { useSelector } from "react-redux";
import { IEmployee } from "Dashboard/scenes/HumanResources/domain/IEmployee";
import { useAppDispatch } from "hooks/hooks";
import DatePicker from "react-datepicker";
import InfoIcon from "./../../../../../images/info-icon.png"
import customStylesInfo from "./SelectStyle";
import InfoTooltip from "components/Popup/InfoTooltip";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { selectState as selectStateDashboard } from "Dashboard/dashboard.slice";
import { hasWriteAccessTo } from "utils/authUtil";
import { HUMAN_RESOURCES_MODULE_NAME } from "Dashboard/scenes/HumanResources/domain/HumanResourcesModule";
import NumberInput from "Dashboard/components/NumberInput";
import EmployeeRoleSelectCreate from "./EmployeeRoleSelectCreate";

interface IProps {
  employee: IEmployee;
}

const PersonInfo = ({ employee }: IProps) => {
  const dispatch = useAppDispatch();

  const { employeeDetails, employeeDetailsLoading: loading } =
    useSelector(selectState);
  const { user } = useSelector(selectStateAuth);
  const { provisions } = useSelector(selectStateDashboard);

  const [healthInsuranceUntil, setHealthInsuranceUntil] = useState<Date | null>(
    null
  );
  const [employedUntil, setEmployedUntil] = useState<Date | null>(null);
  const [birthDate, setBirthDate] = useState<Date | null>(null);
  const [employedAt, setEmployedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (employee) dispatch(getEmployeeDetails(employee.employeeId));
  }, [dispatch, employee?.employeeId]);

  useEffect(() => {
    const healthInsuranceUntilDate = employeeDetails?.healthInsuranceUntil
      ? new Date(employeeDetails.healthInsuranceUntil)
      : null;
    setHealthInsuranceUntil(healthInsuranceUntilDate);

    const employedUntilDate = employeeDetails?.employedUntil
      ? new Date(employeeDetails.employedUntil)
      : null;
    setEmployedUntil(employedUntilDate);

    const birthDate = employeeDetails?.birthDate
      ? new Date(employeeDetails.birthDate)
      : null;
    setBirthDate(birthDate);

    const employedAt = employeeDetails?.employedAt
      ? new Date(employeeDetails.employedAt)
      : null;
    setEmployedAt(employedAt);
  }, [employeeDetails?.employeeId, employeeDetails?.birthDate, employeeDetails?.employedAt,
  employeeDetails?.employedUntil, employeeDetails?.healthInsuranceUntil]);

  const [isOpen, setIsOpen] = useState(false);
  const moreInfoRef = useRef(null);

  useOnClickOutside(moreInfoRef, () => setIsOpen(false));

  const delayedEmployeeDetailsUpdate = useCallback(
    debounce(async (data: IUpdateEmployeeRequestParams) => {
      try {
        dispatch(updateEmployeeDetails(data));
      } catch (err) {
        console.log(err);
      }
    }, 1000),
    []
  );

  const onHourlyRateChanged = (index: number, newRate: number) => {
    if (employeeDetails) {
      let newHourlyRatesWithLabel = [...employeeDetails.hourlyRates];
      newHourlyRatesWithLabel[index] = { ...newHourlyRatesWithLabel[index], rate: newRate };
      const newHourlyRates = newHourlyRatesWithLabel.map(rateWithLabel => rateWithLabel.rate);
      const newEmployeeDetails = { ...employeeDetails, hourlyRates: newHourlyRatesWithLabel };

      dispatch(updateEmployeeDetailsTemporarily(newEmployeeDetails));
      delayedEmployeeDetailsUpdate({
        employeeId: employeeDetails.employeeId,
        hourlyRates: newHourlyRates,
      });
    }
  };

  const onTextChanged = (
    event: ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;

    if (employeeDetails) {
      const newEmployeeDetails = { ...employeeDetails, [name]: value };
      dispatch(updateEmployeeDetailsTemporarily(newEmployeeDetails));

      delayedEmployeeDetailsUpdate({
        employeeId: employeeDetails.employeeId,
        [name]: value,
      });
    }
  };

  const onRegisteredCheckboxChange = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const { checked } = event.target;
    try {
      if (employeeDetails) {
        const newEmployeeDetails = {
          ...employeeDetails,
          registered: checked,
        };
        dispatch(updateEmployeeDetailsTemporarily(newEmployeeDetails));
        delayedEmployeeDetailsUpdate({
          employeeId: employeeDetails.employeeId,
          registered: checked,
        });
      }
    } catch (err) {
      console.log(err);
    }
  };

  const onCarnetCheckboxChange = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const { checked } = event.target;
    const salaryType = checked ? SalaryType.MONTHLY : SalaryType.HOURLY;

    if (employeeDetails) {
      const newEmployeeDetails = {
        ...employeeDetails,
        salaryType,
      };
      dispatch(updateEmployeeDetailsTemporarily(newEmployeeDetails));

      try {
        const data = {
          employeeId: employeeDetails.employeeId,
          salaryType,
        };
        delayedEmployeeDetailsUpdate(data);
      } catch (err) {
        console.log(err);
      }
    }
  };

  const updateHealthInsuranceUntilDate = async (date: Date) => {
    const old_date = healthInsuranceUntil;
    try {
      if (employeeDetails) {
        setHealthInsuranceUntil(date);
        delayedEmployeeDetailsUpdate({
          employeeId: employeeDetails.employeeId,
          healthInsuranceUntil: date ? FormatDateIso(date) : null,
        });
      }
    } catch (err) {
      setHealthInsuranceUntil(old_date);
    }
  };

  const updateEmployedUntilDate = async (date: Date) => {
    const old_date = employedUntil;
    try {
      if (employeeDetails) {
        setEmployedUntil(date);
        dispatch(
          updateEmployeeDetailsTemporarily({
            ...employeeDetails,
            employedUntil: date ? FormatDateIso(date) : null,
          })
        );

        delayedEmployeeDetailsUpdate({
          employeeId: employeeDetails.employeeId,
          employedUntil: date ? FormatDateIso(date) : null,
        });
      }
    } catch (err) {
      setEmployedUntil(old_date);
    }
  };

  const updateBirthDate = async (date: Date) => {
    const old_date = birthDate;
    try {
      if (employeeDetails) {
        setBirthDate(date);
        dispatch(
          updateEmployeeDetailsTemporarily({
            ...employeeDetails,
            birthDate: date ? FormatDateIso(date) : null,
          })
        );
        delayedEmployeeDetailsUpdate({
          employeeId: employeeDetails.employeeId,
          birthDate: date ? FormatDateIso(date) : null,
        });
      }
    } catch (err) {
      setBirthDate(old_date);
      if (employeeDetails) {
        dispatch(
          updateEmployeeDetailsTemporarily({
            ...employeeDetails,
            birthDate: old_date ? FormatDateIso(old_date) : null,
          })
        );
      }
    }
  };

  const updateEmployedAt = async (date: Date) => {
    const old_date = employedAt;
    try {
      if (employeeDetails) {
        setEmployedAt(date);
        dispatch(
          updateEmployeeDetailsTemporarily({
            ...employeeDetails,
            employedAt: date ? FormatDateIso(date) : null,
          })
        );
        delayedEmployeeDetailsUpdate({
          employeeId: employeeDetails.employeeId,
          employedAt: date ? FormatDateIso(date) : null,
        });
      }
    } catch (err) {
      setEmployedAt(old_date);
      if (employeeDetails) {
        dispatch(
          updateEmployeeDetailsTemporarily({
            ...employeeDetails,
            employedAt: old_date ? FormatDateIso(old_date) : null,
          })
        );
      }
    }
  };

  const updateStatus = async (status: StatusType) => {
    try {
      if (employeeDetails) {
        dispatch(
          updateEmployeeDetailsTemporarily({
            ...employeeDetails,
            status,
          })
        );
        delayedEmployeeDetailsUpdate({
          employeeId: employeeDetails.employeeId,
          status,
        });
      }
    } catch (err) { }
  };

  const updateGender = async (gender: GenderType) => {
    try {
      if (employeeDetails) {
        dispatch(
          updateEmployeeDetailsTemporarily({
            ...employeeDetails,
            gender,
          })
        );
        delayedEmployeeDetailsUpdate({
          employeeId: employeeDetails.employeeId,
          gender,
        });
      }
    } catch (err) { }
  };

  const onSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "status") {
      updateStatus(StatusType[value as keyof typeof StatusType]);
    } else if (name === "gender") {
      updateGender(GenderType[value as keyof typeof GenderType]);
    }
  };

  const onRoleSelectChange = async (roleId: number) => {
    try {
      if (employeeDetails) {
        dispatch(
          updateEmployeeDetailsTemporarily({
            ...employeeDetails,
            employeeRole: { roleId: roleId, roleName: '' },
          })
        );

        delayedEmployeeDetailsUpdate({
          employeeId: employeeDetails.employeeId,
          employeeRoleId: roleId,
        });
      }
    } catch (err) {
      console.log(err);
    }
  };

  const displayHourlyRate = (rate: number | undefined): string | undefined => {
    if (employeeDetails?.salaryType === SalaryType.HOURLY && rate !== undefined) {
      return rate?.toString() ?? "";
    }
    return "";
  }

  return (
    <div className="hr-person-info loading-container">
      {loading && <Loader />}
      <span className="hr-person-name">
        <span
          onClick={hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME) ? () => setIsOpen(true) : undefined}
          className={`more-info-tooltip${!hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME) ? ' disabled' : ''}`}
        >
          <InfoTooltip tooltip="Info" element="span"><img src={InfoIcon} alt="" /></InfoTooltip>
          {/* <InfoIcon className="fas" /> */}
          <div
            ref={moreInfoRef}
            className={`hr-person-more-info ${isOpen ? " active" : ""}`}
          >
            <h1>Info</h1>
            <div className="hr-person-content">
              <div className="person-avatar">
                <img src="" alt="" />
              </div>
              <div className="more-info-container">
                <div className="more-info-date-gender">
                  <span>dat rođenja{" "}</span>
                  <div>
                    <DatePicker
                      className="datePicker"
                      placeholderText="Izaberi datum"
                      dateFormat="dd.MM.yyyy"
                      showMonthDropdown
                      showYearDropdown
                      onChange={(date: any) => {
                        updateBirthDate(date);
                      }}
                      selected={birthDate}
                    />
                    <div>
                      <span>pol </span>
                      <select
                        name="gender"
                        value={employeeDetails?.gender}
                        onChange={onSelectChange}
                      >
                        <option label="Muško" value={GenderType.MALE}></option>
                        <option label="Žensko" value={GenderType.FEMALE}></option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="span-2">
                  <span>br.lk.</span>
                  <div>
                    <input
                      type="text"
                      name="governmentId"
                      value={
                        employeeDetails?.governmentId
                          ? employeeDetails?.governmentId
                          : ""
                      }
                      onChange={onTextChanged}
                    ></input>
                  </div>
                </div>
                <div className="span-2">
                  <span>sup</span>
                  <div>
                    <input
                      type="text"
                      name="idIssuedBy"
                      value={
                        employeeDetails?.idIssuedBy
                          ? employeeDetails?.idIssuedBy
                          : ""
                      }
                      onChange={onTextChanged}
                    ></input>
                  </div>
                </div>
                <div className="span-2">
                  <span>telefon</span>
                  <div>
                    <input
                      type="text"
                      name="phone"
                      value={employeeDetails?.phone ? employeeDetails?.phone : ""}
                      onChange={onTextChanged}
                    ></input>
                  </div>
                </div>
                <div className="span-2">
                  <span>adresa</span>
                  <div>
                    <input
                      type="text"
                      name="address"
                      value={
                        employeeDetails?.address ? employeeDetails?.address : ""
                      }
                      onChange={onTextChanged}
                    ></input>
                  </div>
                </div>
                <div className="span-2">
                  <span>radno mesto </span>
                  <div>
                    <EmployeeRoleSelectCreate
                      styles={customStylesInfo}
                      value={employeeDetails?.employeeRole.roleId}
                      onChange={onRoleSelectChange}
                    />
                  </div>
                </div>
                <div className="span-2">
                  <span>primljen </span>
                  <DatePicker
                    className="datePicker"
                    placeholderText="Izaberi datum"
                    showMonthDropdown
                    showYearDropdown
                    dateFormat="dd.MM.yy"
                    onChange={(date: any) => {
                      updateEmployedAt(date);
                    }}
                    selected={employedAt}
                  />
                </div>
              </div>
            </div>
          </div>
        </span>
        {employeeDetails?.employeeName}
      </span>
      <div className="hr-person-info-box">
        <div>
          Status zaposlenog
          <select
            value={employeeDetails?.status}
            name="status"
            onChange={onSelectChange}
            disabled={!hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)}
          >
            <option label="Aktivan" value={StatusType.ACTIVE}></option>
            <option label="Neaktivan" value={StatusType.INACTIVE}></option>
          </select>
        </div>
        <div>
          Prijavljen do{" "}
          <input
            type="checkbox"
            checked={employeeDetails?.registered ? true : false}
            onChange={hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME) ? onRegisteredCheckboxChange : undefined}
            disabled={!hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)}
          />
          <div>
            <DatePicker
              className="rounded-datePicker"
              dateFormat="dd.MM.yy"
              showMonthDropdown
              showYearDropdown
              onChange={(date: any) => {
                updateEmployedUntilDate(date);
              }}
              selected={employedUntil}
              disabled={!hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)}
            />
          </div>
        </div>
        <div>
          Sledeći zdravstveni
          <div>
            <DatePicker
              className="rounded-datePicker"
              dateFormat="dd.MM.yy"
              showMonthDropdown
              showYearDropdown
              onChange={(date: any) => {
                updateHealthInsuranceUntilDate(date);
              }}
              selected={healthInsuranceUntil}
              disabled={!hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)}
            />
          </div>
        </div>
        <div>
          Nije na karnete{" "}
          <input
            className="double-border"
            type="checkbox"
            checked={employeeDetails?.salaryType === SalaryType.MONTHLY}
            onChange={hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME) ? onCarnetCheckboxChange : undefined}
            disabled={!hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)}
          />
        </div>
        <div
          className={
            employeeDetails?.salaryType === SalaryType.MONTHLY ? "disabled" : ""
          }
        >
          Cene karneta
          {provisions?.activeHourlyRateNames.map((rateName, index) =>
          (<React.Fragment key={index}>
            <NumberInput
              formatAs="quantity"
              value={displayHourlyRate(employeeDetails?.hourlyRates?.[rateName.index]?.rate)}
              placeholder={rateName.name}
              onChange={hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)
                ? (value: string, unmaskedValue: string, parsedValue: number) => {
                  if (value !== displayHourlyRate(employeeDetails?.hourlyRates?.[rateName.index]?.rate)) {
                    onHourlyRateChanged(rateName.index, parsedValue);
                  }
                }
                : undefined}
              className="text"
              disabled={!hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)}
              title={rateName.name}
            />
            {index % 2 === 1 && (<div></div>)}
          </React.Fragment>)
          )}
        </div>
      </div>
      <div className="hr-person-note">
        <textarea
          name="notes"
          value={employeeDetails?.notes}
          onChange={hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME) ? onTextChanged : undefined}
          disabled={!hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)}
        ></textarea>
      </div>
    </div>
  );
};

export default PersonInfo;
